import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useSeo } from "../lib/seo";

/**
 * Operations-center sections (Phase 3). Every section carries a `roles` array
 * so we can gate visibility later without a redesign. Today only `super_admin`
 * (via the alias "owner") exists; future roles will simply extend the array.
 *
 * ⚠️ Only `super_admin` sees this page at all — see the auth gate below. The
 * per-section `roles` array is the SEED of a future permission system.
 */
const OPS_SECTIONS = [
  { key: "overview",   label: "Platform Overview", to: "#overview-panel",     roles: ["owner", "support", "moderator", "finance"] },
  { key: "businesses", label: "Business Management", to: "#businesses-panel", roles: ["owner", "support", "moderator"] },
  { key: "customers",  label: "Customer Management", to: "#customers-panel",  roles: ["owner", "support"] },
  { key: "cases",      label: "Cases",     to: "/admin/cases",                roles: ["owner", "moderator"] },
  { key: "reports",    label: "Reports",   to: "/admin/cases?filter=urgent",  roles: ["owner", "moderator"] },
  { key: "financial",  label: "Financial", to: "#financial-panel",            roles: ["owner", "finance"] },
  { key: "stripe",     label: "Stripe",    to: "#stripe-panel",               roles: ["owner", "finance"] },
  { key: "email",      label: "Email",     to: "#email-panel",                roles: ["owner"] },
  { key: "ai",         label: "AI Moderation", to: "#ai-panel",               roles: ["owner", "moderator"] },
  { key: "categories", label: "Categories", to: "#categories-panel",          roles: ["owner"] },
  { key: "audit",      label: "Audit Logs", to: "#audit-panel",               roles: ["owner"] },
  { key: "diagnostics", label: "System Diagnostics", to: "/admin/diagnostics", roles: ["owner"] },
  { key: "ai-monitoring", label: "AI Monitoring", to: "/admin/ai-monitoring", roles: ["owner"] },
  { key: "analytics",  label: "Analytics",  to: "#analytics-panel",           roles: ["owner", "finance"] },
  { key: "settings",   label: "Platform Settings", to: "#settings-panel",     roles: ["owner"] },
];

function OpsNav({ activeRole = "owner" }) {
  const items = OPS_SECTIONS.filter((s) => s.roles.includes(activeRole));
  const nav = (to) => {
    if (to && to.startsWith("#")) {
      const el = document.querySelector(to);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div className="rounded-2xl border p-3 mb-6 flex items-center gap-1 flex-wrap"
         style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
         data-testid="ops-nav">
      <div className="text-[10px] uppercase tracking-[0.25em] pr-3 pl-1" style={{ color: "var(--gf-text-muted)" }}>
        Operations
      </div>
      {items.map((s) => {
        const cls = "text-xs rounded-full px-3 py-1.5 hover:bg-black/5";
        if (s.to && s.to.startsWith("#")) {
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => nav(s.to)}
              className={cls}
              data-testid={`ops-${s.key}`}
              style={{ color: "var(--gf-text)" }}
            >
              {s.label}
            </button>
          );
        }
        if (s.to) {
          return (
            <Link key={s.key} to={s.to} className={cls}
                  data-testid={`ops-${s.key}`}
                  style={{ color: "var(--gf-text)" }}>
              {s.label}
            </Link>
          );
        }
        return (
          <span key={s.key} className={cls + " opacity-40 cursor-not-allowed"}
                data-testid={`ops-${s.key}`}
                style={{ color: "var(--gf-text-muted)" }}>
            {s.label} <span className="text-[9px] opacity-70">soon</span>
          </span>
        );
      })}
    </div>
  );
}

const STATUS_TABS = [
  { key: "approved", label: "Active" },
  { key: "suspended", label: "Suspended" },
  { key: "rejected", label: "Rejected" },
];

const SUSPEND_REASONS = [
  { value: "moderation", label: "Moderation (policy violation)" },
  { value: "fraud_investigation", label: "Fraud investigation" },
  { value: "support_request", label: "Support request" },
  { value: "dispute_review", label: "Dispute review" },
  { value: "legal_compliance", label: "Legal / compliance" },
];

const STATUS_TAG = {
  approved: { color: "var(--gf-on-accent)", bg: "var(--gf-accent)" },
  rejected: { color: "#fff", bg: "#dc2626" },
  suspended: { color: "#fff", bg: "#dc2626" },
};

export default function AdminDashboard() {
  useSeo({ title: "Admin — BookFreely", path: "/admin", noindex: true });
  const { user, authHeader, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("approved");
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState({});
  const [busy, setBusy] = useState(null); // {id, action}
  const [search, setSearch] = useState("");

  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = isSuperAdmin;

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      nav("/");
    }
  }, [user, loading, nav, isAdmin]);

  const load = async () => {
    if (!user || !isAdmin) return;
    try {
      // "suspended" is an `account_status` filter; everything else maps to `admin_status`.
      const params = tab === "suspended"
        ? { account_status: "deactivated" }
        : { status_filter: tab };
      const [list, s] = await Promise.all([
        axios.get(`${API}/admin/businesses`, { params, headers: authHeader() }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setBusinesses(list.data.businesses || []);
      setStats(s.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load admin data");
    }
  };

  useEffect(() => { load();   }, [tab, user?.id]);

  // Modal-driven actions (replaces window.prompt which was inconsistent across browsers)
  const [actionModal, setActionModal] = useState(null); // { business, action }
  // Bump whenever a business/customer action succeeds so all Ops panels
  // re-fetch their KPIs — otherwise the sidebar KPIs stay stale after e.g.
  // a permanent delete until the whole page is hard-refreshed.
  const [opsRefreshTick, setOpsRefreshTick] = useState(0);

  const runAction = async ({ business: b, action, payload }) => {
    setBusy({ id: b.id, action });
    try {
      let url, body;
      if (action === "extend_trial") {
        url = `${API}/admin/businesses/${b.id}/extend_trial`; body = { days: payload.days };
      } else if (action === "end_trial") {
        url = `${API}/admin/businesses/${b.id}/end_trial`; body = {};
      } else if (action === "suspend" || action === "unsuspend") {
        url = `${API}/admin/businesses/${b.id}/${action}`;
        body = { reason: payload.reason, note: payload.note || null };
      } else if (action === "delete_permanent") {
        url = `${API}/admin/businesses/${b.id}/delete-permanent`;
        body = {
          password: payload.password,
          step_up_code: payload.step_up_code,
          confirm_business_name: payload.confirm_business_name,
          reason: payload.reason,
        };
      } else if (action === "reconcile_subscription") {
        url = `${API}/admin/businesses/${b.id}/subscription/reconcile`;
        body = {};
      } else {
        throw new Error(`Unknown action: ${action}`);
      }
      const r = await axios.post(url, body, { headers: authHeader() });
      toast.success(
        action === "suspend" ? `Suspended ${b.business_name}`
        : action === "unsuspend" ? `Unsuspended ${b.business_name}`
        : action === "end_trial" ? `Trial ended for ${b.business_name}`
        : action === "extend_trial" ? `Trial extended to ${new Date(r.data.trial_end_at || r.data.trial_listing_until).toLocaleDateString()}`
        : action === "delete_permanent" ? `${b.business_name} permanently deleted (${r.data.future_bookings_cancelled} booking(s) cancelled)`
        : action === "reconcile_subscription" ? (r.data.changed
            ? `Synced ${b.business_name} · applied ${r.data.applied_fields.join(", ")} · status=${r.data.user.subscription_status || "-"}`
            : ((r.data.stripe_snapshot?.subscriptions || []).length > 0
                ? `${b.business_name} already in sync with Stripe (${(r.data.stripe_snapshot.subscriptions[0] || {}).status})`
                : `${b.business_name}: no subscription found on Stripe for customer ${r.data.user.stripe_customer_id || "(none)"}`))
        : "Done"
      );
      setActionModal(null);
      load();
      // Notify every Ops panel to re-fetch — active-business KPIs go stale
      // otherwise after a delete/suspend/unsuspend/trial change.
      setOpsRefreshTick((t) => t + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Action failed";
      // Stripe / server errors are long — give admins time to read them.
      toast.error(detail, { duration: 12000 });
    } finally {
      setBusy(null);
    }
  };

  const openAction = (b, action) => {
    // Sync from Stripe needs no confirmation — run inline.
    if (action === "reconcile_subscription") {
      runAction({ business: b, action, payload: {} });
      return;
    }
    // For end_trial / unsuspend, no extra info needed — confirm + run inline
    setActionModal({ business: b, action });
  };

  // Keep act() name so existing JSX onAction={act} keeps working
  const act = openAction;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter((b) =>
      [b.business_name, b.email, b.full_name, b.business_category, b.contact_phone, b.website]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [businesses, search]);

  if (loading || !user || !isAdmin) {
    return (
      <div className="App">
        <Header hideSearch />
        <div className="max-w-3xl mx-auto p-12 text-sm" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="App" data-testid="admin-dashboard">
      <Header hideSearch />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <OpsNav />
        <div id="businesses-panel" className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Operations</div>
            <h1 className="font-display text-4xl mt-1" style={{ color: "var(--gf-text)" }}>Business management</h1>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--gf-text-muted)" }}>
              Businesses are auto-approved once Stripe Connect KYC is complete. Use this page to search, suspend, or investigate businesses on the platform.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {STATUS_TABS.map((t) => (
              <div key={t.key} className="rounded-xl border px-3 py-2 min-w-[80px]" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>{t.label}</div>
                <div className="font-display text-2xl mt-0.5" style={{ color: "var(--gf-text)" }} data-testid={`stat-${t.key}`}>
                  {stats[t.key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2 flex-wrap">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="rounded-full px-4 py-1.5 text-sm border transition-colors flex items-center gap-1.5"
              style={tab === t.key
                ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)", borderColor: "var(--gf-accent)" }
                : { background: "transparent", color: "var(--gf-text)", borderColor: "var(--gf-border-strong)" }}
            >
              {t.label}
              {(stats[t.key] ?? 0) > 0 && (
                <span className="rounded-full px-1.5 text-[10px]" style={{ background: tab === t.key ? "var(--gf-on-accent)" : "var(--gf-surface-2)", color: tab === t.key ? "var(--gf-accent)" : "var(--gf-text)" }}>
                  {stats[t.key]}
                </span>
              )}
            </button>
          ))}
          {stats.safety_flags_open > 0 && (
            <a href="#safety-flags-panel" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="safety-flags-panel"]')?.scrollIntoView({ behavior: "smooth" }); }}
               data-testid="safety-flags-quick-badge"
               className="ml-2 inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 border"
               style={{ borderColor: "var(--gf-danger)", background: "var(--gf-bg)", color: "var(--gf-danger)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gf-danger)" }} />
              {stats.safety_flags_open} open safety report{stats.safety_flags_open > 1 ? "s" : ""}
            </a>
          )}
          <input
            data-testid="admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, website…"
            className="ml-auto bg-transparent border rounded-full px-4 py-1.5 text-sm outline-none focus:border-[var(--gf-accent)] w-64"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
        </div>

        <div className="mt-6 space-y-3">
          {filtered.length === 0 ? (
            <div className="rounded-xl border p-8 text-center text-sm" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }} data-testid="admin-empty-state">
              No businesses in this state.
            </div>
          ) : (
            filtered.map((b) => (
              <BusinessRow
                key={b.id}
                b={b}
                onAction={act}
                busy={busy && busy.id === b.id ? busy.action : null}
                isSuperAdmin={isSuperAdmin}
              />
            ))
          )}
        </div>

        {isSuperAdmin && <OverviewPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <SafetyFlagsPanel authHeader={authHeader} />}
        {isSuperAdmin && <CustomerSearchPanel authHeader={authHeader} />}
        {isAdmin && <PromoCodesPanel authHeader={authHeader} />}
        {isSuperAdmin && <FinancialPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <StripePanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <EmailHealthPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <AiModerationPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <CategoriesPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <AnalyticsPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
        {isSuperAdmin && <AuditLogPanel authHeader={authHeader} />}
        {isSuperAdmin && <SettingsPanel authHeader={authHeader} refreshTick={opsRefreshTick} />}
      </div>
      <Footer />

      {actionModal && (
        <ActionModal
          business={actionModal.business}
          action={actionModal.action}
          onClose={() => setActionModal(null)}
          onSubmit={(payload) => runAction({ business: actionModal.business, action: actionModal.action, payload })}
          submitting={!!busy && busy.id === actionModal.business.id && busy.action === actionModal.action}
          authHeader={authHeader}
        />
      )}
    </div>
  );
}

function BusinessRow({ b, onAction, busy, isSuperAdmin }) {
  const tag = STATUS_TAG[b.admin_status] || STATUS_TAG.approved;
  const trialEnd = b.trial_end_at ? new Date(b.trial_end_at) : null;
  const trialActive = !!trialEnd && trialEnd > new Date();
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;
  return (
    <div
      data-testid={`biz-row-${b.id}`}
      className="rounded-2xl border p-5"
      style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
    >
      <div className="flex flex-wrap items-start gap-4">
        {b.profile_image_url ? (
          <img src={b.profile_image_url} alt="" className="w-14 h-14 rounded-full object-cover border" style={{ borderColor: "var(--gf-border-strong)" }} />
        ) : (
          <div className="w-14 h-14 rounded-full border flex items-center justify-center font-display text-xl" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)", background: "var(--gf-bg-soft)" }}>
            {(b.business_name || b.full_name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl" style={{ color: "var(--gf-text)" }} data-testid={`biz-name-${b.id}`}>{b.business_name || b.full_name}</h3>
            <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: tag.bg, color: tag.color }} data-testid={`biz-status-${b.id}`}>
              {b.admin_status.replace("_", " ")}
            </span>
            {b.is_verified && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                Public · Verified
              </span>
            )}
            {trialActive && !b.is_verified && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "var(--gf-surface-2)", color: "var(--gf-accent)" }} data-testid={`trial-pill-${b.id}`}>
                Trial · {trialDaysLeft}d left
              </span>
            )}
            {trialActive && b.is_verified && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "var(--gf-surface-2)", color: "var(--gf-accent)" }} data-testid={`trial-pill-${b.id}`}>
                In trial · {trialDaysLeft}d
              </span>
            )}
            {b.account_status === "deactivated" && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "#dc2626", color: "#fff" }} data-testid={`deactivated-pill-${b.id}`}>
                Suspended
              </span>
            )}
            {b.account_status === "deleting" && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "#7c2d12", color: "#fff" }} data-testid={`deleting-pill-${b.id}`}>
                Pending delete
              </span>
            )}
            {b.bookings_paused && b.account_status !== "deactivated" && (
              <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ background: "#d97706", color: "#fff" }} data-testid={`paused-pill-${b.id}`}>
                Bookings paused
              </span>
            )}
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>
            {b.business_category || "Uncategorised"} · Owner: {b.full_name}
          </div>
          {b.business_description && (
            <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--gf-text-soft)" }}>
              {b.business_description}
            </p>
          )}
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs" style={{ color: "var(--gf-text-soft)" }}>
            <div><span style={{ color: "var(--gf-text-muted)" }}>Email:</span> {b.email}</div>
            <div><span style={{ color: "var(--gf-text-muted)" }}>Phone:</span> {b.contact_phone || "—"}</div>
            <div>
              <span style={{ color: "var(--gf-text-muted)" }}>Website:</span>{" "}
              {b.website ? <a href={b.website} target="_blank" rel="noreferrer" className="underline">{b.website.replace(/^https?:\/\//, "")}</a> : "—"}
            </div>
            <div><span style={{ color: "var(--gf-text-muted)" }}>Joined:</span> {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <StripeChip ok={b.stripe_charges_enabled} label="Stripe charges" />
            <StripeChip ok={b.stripe_payouts_enabled} label="Stripe payouts" />
            <StripeChip ok={b.stripe_details_submitted} label="Stripe KYC submitted" />
          </div>

          {b.admin_notes && (
            <div className="mt-3 rounded-md border p-2 text-xs" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)", color: "var(--gf-text-soft)" }} data-testid={`biz-notes-${b.id}`}>
              <span className="uppercase tracking-[0.18em] text-[10px] block mb-1" style={{ color: "var(--gf-text-muted)" }}>Admin note</span>
              {b.admin_notes}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-[140px]">
          {b.account_status === "deactivated" ? (
            <button
              data-testid={`unsuspend-${b.id}`}
              onClick={() => onAction(b, "unsuspend")}
              disabled={!!busy}
              className="rounded-full px-4 py-2 text-sm border disabled:opacity-50"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {busy === "unsuspend" ? "…" : "Unsuspend"}
            </button>
          ) : (
            <button
              data-testid={`suspend-${b.id}`}
              onClick={() => onAction(b, "suspend")}
              disabled={!!busy}
              className="rounded-full px-4 py-2 text-sm border disabled:opacity-50"
              style={{ borderColor: "#dc2626", color: "#dc2626" }}
            >
              {busy === "suspend" ? "…" : "Suspend"}
            </button>
          )}
          {isSuperAdmin && (
            <button
              data-testid={`reconcile-sub-${b.id}`}
              onClick={() => onAction(b, "reconcile_subscription")}
              disabled={!!busy}
              title="Force-sync this business's subscription state from Stripe. Safe to click anytime — idempotent."
              className="rounded-full px-4 py-2 text-sm border disabled:opacity-50"
              style={{ borderColor: "var(--gf-surface-2)", color: "var(--gf-accent)" }}
            >
              {busy === "reconcile_subscription" ? "Syncing…" : "Sync from Stripe"}
            </button>
          )}
          {isSuperAdmin && b.admin_status === "approved" && (
            <>
              <button
                data-testid={`extend-trial-${b.id}`}
                onClick={() => onAction(b, "extend_trial")}
                disabled={!!busy}
                className="rounded-full px-4 py-2 text-sm border disabled:opacity-50"
                style={{ borderColor: "var(--gf-surface-2)", color: "var(--gf-accent)" }}
              >
                {busy === "extend_trial" ? "…" : "Extend trial"}
              </button>
              {trialActive && (
                <button
                  data-testid={`end-trial-${b.id}`}
                  onClick={() => onAction(b, "end_trial")}
                  disabled={!!busy}
                  className="rounded-full px-4 py-2 text-xs border disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}
                >
                  {busy === "end_trial" ? "…" : "End trial now"}
                </button>
              )}
            </>
          )}
          {isSuperAdmin && (
            <button
              data-testid={`delete-permanent-${b.id}`}
              onClick={() => onAction(b, "delete_permanent")}
              disabled={!!busy}
              className="rounded-full px-4 py-2 text-xs font-medium border disabled:opacity-50"
              style={{ borderColor: "#7f1d1d", color: "#7f1d1d", background: "rgba(220,38,38,0.08)" }}
              title="Permanently anonymize this business (irreversible)"
            >
              Delete permanently
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StripeChip({ ok, label }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
      style={{
        background: ok ? "rgba(76,175,80,0.12)" : "var(--gf-bg-soft)",
        color: ok ? "#15803d" : "var(--gf-text-muted)",
      }}
    >
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: ok ? "#16a34a" : "var(--gf-border-strong)" }} />
      {label}
    </span>
  );
}

function AuditLogPanel({ authHeader }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [filterAction, setFilterAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit-log`, {
        params: { limit: 100, ...(filterAction ? { action: filterAction } : {}) },
        headers: authHeader(),
      });
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load();   }, [open, filterAction]);

  return (
    <div id="audit-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="audit-log-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Audit log</h2>
          <p className="text-xs mt-1 max-w-md" style={{ color: "var(--gf-text-muted)" }}>
            Every sensitive admin action is recorded here — search, suspend, view payments, view threads, view disputes, factory reset.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {open && (
            <input
              data-testid="audit-filter-action"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              placeholder="filter by action (e.g. suspend_business)"
              className="bg-transparent border rounded-full px-3 py-1.5 text-xs outline-none focus:border-[var(--gf-accent)] w-60"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          )}
          <button
            data-testid="audit-toggle-btn"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full px-4 py-1.5 text-sm border"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            {open ? "Close" : "Open log"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5">
          {loading ? (
            <div className="text-sm" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="audit-empty">No entries.</div>
          ) : (
            <div className="space-y-2" data-testid="audit-entries">
              {entries.map((e) => (
                <div key={e.id} className="rounded-lg border p-3 text-xs" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid={`audit-${e.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono" style={{ color: "var(--gf-text)" }}>{e.action}</span>
                    <span style={{ color: "var(--gf-text-muted)" }}>by</span>
                    <span style={{ color: "var(--gf-text)" }}>{e.admin_email}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] rounded-full px-1.5 py-0.5" style={{ background: e.admin_role === "super_admin" ? "var(--gf-accent)" : "var(--gf-bg-soft)", color: e.admin_role === "super_admin" ? "var(--gf-on-accent)" : "var(--gf-text-muted)" }}>
                      {e.admin_role}
                    </span>
                    <span className="ml-auto" style={{ color: "var(--gf-text-muted)" }}>{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  {(e.target_type || e.target_id) && (
                    <div className="mt-1" style={{ color: "var(--gf-text-soft)" }}>
                      Target: <span className="font-mono">{e.target_type || "—"}:{e.target_id || "—"}</span>
                    </div>
                  )}
                  {e.reason && (
                    <div className="mt-1" style={{ color: "var(--gf-text-soft)" }}>
                      Reason: <span style={{ color: "var(--gf-text)" }}>{e.reason.replace(/_/g, " ")}</span>
                      {e.note ? <span style={{ color: "var(--gf-text-muted)" }}> — {e.note}</span> : null}
                    </div>
                  )}
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <pre className="mt-1 text-[10px] overflow-x-auto" style={{ color: "var(--gf-text-muted)" }}>{JSON.stringify(e.metadata)}</pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------- Customer search (super_admin only) ----------
function CustomerSearchPanel({ authHeader }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  // Consolidated action state: `mode` = null | "suspend" | "delete"
  const [action, setAction] = useState({ mode: null, reason: "", note: "", confirm: "", step_up_code: "" });
  const resetAction = () => setAction({ mode: null, reason: "", note: "", confirm: "", step_up_code: "" });

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filter && filter !== "all") params.filter = filter;
      const r = await axios.get(`${API}/admin/customers/search`, { params, headers: authHeader() });
      setResults(r.data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load customers");
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/customers/${id}/details`, { headers: authHeader() });
      setDetails(r.data);
      resetAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open customer");
    }
  };

  const suspend = async () => {
    if (!action.reason || action.reason.trim().length < 5) return toast.error("Please enter a reason (≥ 5 characters).");
    setActionBusy("suspend");
    try {
      await axios.post(
        `${API}/admin/customers/${details.profile.id}/suspend`,
        { reason: action.reason.trim(), note: action.note.trim() || undefined },
        { headers: authHeader() }
      );
      toast.success("Customer suspended");
      resetAction();
      await search();
      await openDetails(details.profile.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suspend failed");
    } finally { setActionBusy(""); }
  };

  const unsuspend = async () => {
    const reason = window.prompt("Reason for unsuspending (required, ≥ 5 chars):");
    if (!reason || reason.trim().length < 5) { toast.error("Reason must be at least 5 characters."); return; }
    setActionBusy("unsuspend");
    try {
      await axios.post(
        `${API}/admin/customers/${details.profile.id}/unsuspend`,
        { reason: reason.trim() },
        { headers: authHeader() }
      );
      toast.success("Customer unsuspended");
      await search();
      await openDetails(details.profile.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsuspend failed");
    } finally { setActionBusy(""); }
  };

  const requestStepUpCode = async () => {
    try {
      // Reuse the SAME endpoint the working business-delete flow uses. There
      // is exactly one step-up endpoint on the backend (`/admin/step-up/send-code`);
      // the earlier `/admin/step-up/request` call was 404 because that route
      // never existed. The `purpose` field is stored on the code doc for audit.
      await axios.post(
        `${API}/admin/step-up/send-code`,
        { purpose: "delete_customer" },
        { headers: authHeader() }
      );
      toast.info("A step-up code was emailed to your admin address.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send step-up code");
    }
  };

  const permanentDelete = async () => {
    // Simplified: one typed confirmation (email OR literal "DELETE") + step-up code
    const typed = (action.confirm || "").trim();
    const expectedEmail = (details.profile.email || "").trim().toLowerCase();
    const okConfirm = typed.toLowerCase() === expectedEmail || typed.toUpperCase() === "DELETE";
    if (!okConfirm) return toast.error(`Type "${expectedEmail}" or "DELETE" to confirm.`);
    if (!action.step_up_code) return toast.error("Enter your step-up code (check your admin email).");
    setActionBusy("delete");
    try {
      await axios.post(
        `${API}/admin/customers/${details.profile.id}/delete-permanent`,
        {
          reason: "Permanent delete via admin panel",
          note: action.note.trim() || undefined,
          confirm_email: expectedEmail,
          step_up_code: action.step_up_code.trim(),
        },
        { headers: authHeader() }
      );
      toast.success("Customer permanently deleted");
      setDetails(null);
      resetAction();
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setActionBusy(""); }
  };

  // Shared input class + style for dialogs — enforces readable contrast in
  // BOTH light and dark modes (explicit foreground colour, tuned placeholder),
  // plus a clearly visible focus ring so admins can see which field is active.
  const dialogInput = "w-full rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400 focus:border-red-500 placeholder:text-neutral-400";
  const dialogInputStyle = {
    background: "#ffffff",
    color: "#111827",
    border: "1px solid #d1d5db",
  };

  return (
    <div id="customers-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="customer-search-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Customers</h2>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
            Search, filter, suspend or permanently delete customers. Opening a record logs the access.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            data-testid="customer-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-transparent border rounded-full px-3 py-1.5 text-xs outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="owns_business">Owns a business</option>
            <option value="never_booked">Never booked</option>
          </select>
          <input
            data-testid="customer-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="name, email or phone"
            className="bg-transparent border rounded-full px-3 py-1.5 text-xs outline-none focus:border-[var(--gf-accent)] w-60"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <button data-testid="customer-search-btn" onClick={search} disabled={loading}
            className="rounded-full px-4 py-1.5 text-sm" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {loading ? "…" : "Search"}
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="mt-5 grid gap-2" data-testid="customer-search-results">
          {results.map((c) => {
            const suspended = c.admin_status === "suspended";
            return (
              <button key={c.id} onClick={() => openDetails(c.id)}
                data-testid={`customer-row-${c.id}`}
                className="text-left rounded-lg border p-3 flex items-center gap-3 hover:opacity-90 flex-wrap"
                style={{ borderColor: suspended ? "#dc2626" : "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "var(--gf-text)" }}>
                    {c.full_name}
                    {suspended && <span className="ml-2 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "#fee2e2", color: "#991b1b" }}>Suspended</span>}
                    {c.owns_business && <span className="ml-2 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "#eef2ff", color: "#4338ca" }}>Owns business</span>}
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--gf-text-muted)" }}>
                    {c.email}{c.phone ? ` · ${c.phone}` : ""}{c.city ? ` · ${c.city}, ${c.state || ""}` : ""}
                    {c.business_handle && <span className="ml-2" style={{ color: "var(--gf-accent)" }}>@{c.business_handle}</span>}
                  </div>
                </div>
                <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
                  {c.bookings_total} bookings · {c.bookings_completed} done
                </div>
              </button>
            );
          })}
        </div>
      )}

      {details && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => { setDetails(null); resetAction(); }} data-testid="customer-details-modal">
          <div className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
            style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Customer</div>
                <h3 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>{details.profile.full_name}</h3>
                <div className="text-sm mt-0.5" style={{ color: "var(--gf-text-soft)" }}>{details.profile.email}</div>
              </div>
              <button onClick={() => { setDetails(null); resetAction(); }} className="text-xs underline" style={{ color: "var(--gf-text-muted)" }}>Close</button>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <Stat label="Bookings" value={details.stats.bookings_total} />
              <Stat label="Completed" value={details.stats.by_status?.completed || 0} />
              <Stat label="Spent" value={`$${details.stats.spent_completed.toFixed(2)}`} />
              <Stat label="Businesses" value={details.stats.unique_businesses} />
            </div>

            <div className="mt-5 text-xs grid grid-cols-2 gap-x-3 gap-y-1" style={{ color: "var(--gf-text-soft)" }}>
              {details.profile.phone && <div><b style={{ color: "var(--gf-text)" }}>Phone:</b> {details.profile.phone}</div>}
              {details.profile.city && <div><b style={{ color: "var(--gf-text)" }}>City:</b> {details.profile.city}{details.profile.state ? `, ${details.profile.state}` : ""}</div>}
              {details.profile.last_login_at && <div><b style={{ color: "var(--gf-text)" }}>Last login:</b> {new Date(details.profile.last_login_at).toLocaleString()}</div>}
              {details.profile.last_login_ip && <div><b style={{ color: "var(--gf-text)" }}>Last IP:</b> <span className="font-mono">{details.profile.last_login_ip}</span></div>}
              <div><b style={{ color: "var(--gf-text)" }}>Joined:</b> {details.profile.created_at?.slice(0,10)}</div>
            </div>

            {/* Actions row */}
            <div className="mt-5 flex flex-wrap gap-2 pt-4 border-t" style={{ borderColor: "var(--gf-border)" }} data-testid="customer-actions">
              {details.profile.admin_status === "suspended" ? (
                <button
                  onClick={unsuspend}
                  disabled={!!actionBusy}
                  data-testid="customer-unsuspend-btn"
                  className="rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium min-h-[44px] sm:min-h-0"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  {actionBusy === "unsuspend" ? "Working…" : "Unsuspend"}
                </button>
              ) : (
                <button
                  onClick={() => setAction({ ...action, mode: action.mode === "suspend" ? null : "suspend" })}
                  disabled={!!actionBusy}
                  data-testid="customer-suspend-btn"
                  className="rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] min-h-[44px] sm:min-h-0"
                  style={{ borderColor: "#dc2626", color: "#dc2626" }}
                >
                  {action.mode === "suspend" ? "Cancel" : "Suspend"}
                </button>
              )}
              <button
                onClick={() => setAction({ ...action, mode: action.mode === "delete" ? null : "delete" })}
                disabled={!!actionBusy}
                data-testid="customer-delete-perm-toggle"
                className="rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] ml-auto min-h-[44px] sm:min-h-0"
                style={{ background: "#dc2626", color: "#fff" }}
              >
                {action.mode === "delete" ? "Cancel" : "Permanent delete"}
              </button>
            </div>

            {/* Suspend inline form — reason + optional note + confirm */}
            {action.mode === "suspend" && (
              <div
                className="mt-4 rounded-xl border p-4 space-y-3"
                style={{ borderColor: "#fca5a5", background: "#fef2f2" }}
                data-testid="customer-suspend-form"
              >
                <div className="text-sm" style={{ color: "#7f1d1d" }}>
                  <b>Suspending temporarily hides this customer.</b> They will not be able to book new services until unsuspended.
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7f1d1d" }}>Reason (required)</label>
                  <input
                    data-testid="customer-suspend-reason"
                    autoFocus
                    value={action.reason}
                    onChange={(e) => setAction({ ...action, reason: e.target.value })}
                    placeholder="e.g. fraud investigation, chargeback dispute"
                    className={dialogInput}
                    style={dialogInputStyle}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7f1d1d" }}>Note (optional)</label>
                  <textarea
                    data-testid="customer-suspend-note"
                    rows={2}
                    value={action.note}
                    onChange={(e) => setAction({ ...action, note: e.target.value })}
                    placeholder="Any extra context…"
                    className={dialogInput}
                    style={dialogInputStyle}
                  />
                </div>
                <button
                  onClick={suspend}
                  disabled={actionBusy === "suspend"}
                  data-testid="customer-suspend-confirm"
                  className="rounded-full px-4 py-2 text-xs font-medium min-h-[44px] sm:min-h-0"
                  style={{ background: "#dc2626", color: "#fff" }}
                >
                  {actionBusy === "suspend" ? "Suspending…" : "Confirm suspend"}
                </button>
              </div>
            )}

            {/* Permanent-delete simplified form */}
            {action.mode === "delete" && (
              <div
                className="mt-4 rounded-xl border p-4 space-y-3"
                style={{ borderColor: "#dc2626", background: "#fef2f2" }}
                data-testid="customer-delete-perm-form"
              >
                <div className="text-sm" style={{ color: "#7f1d1d" }}>
                  <b>This action is irreversible.</b> The customer's account, notifications, favourites, messages and login history will be permanently removed. Bookings and reviews are kept for accounting with all identifiers scrubbed.
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7f1d1d" }}>
                    Type <span className="font-mono">{details.profile.email}</span> or <span className="font-mono">DELETE</span> to confirm
                  </label>
                  <input
                    data-testid="customer-delete-perm-confirmtext"
                    autoFocus
                    value={action.confirm}
                    onChange={(e) => setAction({ ...action, confirm: e.target.value })}
                    placeholder="Type email or DELETE"
                    className={dialogInput}
                    style={dialogInputStyle}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7f1d1d" }}>Step-up code (from your admin email)</label>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      data-testid="customer-delete-perm-code"
                      value={action.step_up_code}
                      onChange={(e) => setAction({ ...action, step_up_code: e.target.value })}
                      placeholder="6-digit code"
                      inputMode="numeric"
                      className={dialogInput + " flex-1 min-w-[160px]"}
                      style={dialogInputStyle}
                    />
                    <button
                      type="button"
                      onClick={requestStepUpCode}
                      className="rounded-md border px-3 py-2 text-xs whitespace-nowrap"
                      style={{ borderColor: "#dc2626", color: "#dc2626", background: "#ffffff" }}
                    >
                      Email me a code
                    </button>
                  </div>
                </div>
                <button
                  onClick={permanentDelete}
                  disabled={actionBusy === "delete"}
                  data-testid="customer-delete-perm-confirm"
                  className="rounded-full px-4 py-2 text-xs font-medium min-h-[44px] sm:min-h-0"
                  style={{ background: "#dc2626", color: "#fff" }}
                >
                  {actionBusy === "delete" ? "Deleting…" : "Delete permanently"}
                </button>
              </div>
            )}

            {details.favorite_businesses?.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Most-booked businesses</div>
                <ul className="space-y-1">
                  {details.favorite_businesses.map((b) => (
                    <li key={b.business_id} className="flex items-center justify-between text-sm rounded-md border px-3 py-2"
                      style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
                      <span style={{ color: "var(--gf-text)" }}>{b.business_name}</span>
                      <span className="text-xs" style={{ color: "var(--gf-text-muted)" }}>{b.bookings} booking{b.bookings === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {details.recent_bookings?.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Recent bookings</div>
                <div className="space-y-2">
                  {details.recent_bookings.slice(0, 10).map((b) => (
                    <div key={b.id} className="rounded-md border p-3 text-xs" style={{ borderColor: "var(--gf-border)" }}>
                      <div className="flex items-center justify-between">
                        <span style={{ color: "var(--gf-text)" }}>{b.service_title}</span>
                        <span style={{ color: "var(--gf-text-muted)" }}>{b.status}</span>
                      </div>
                      <div className="mt-0.5" style={{ color: "var(--gf-text-muted)" }}>
                        with {b.business_name} · {new Date(b.scheduled_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border p-2" style={{ borderColor: "var(--gf-border)" }}>
      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
      <div className="font-display text-xl mt-0.5" style={{ color: "var(--gf-text)" }}>{value}</div>
    </div>
  );
}

// ---------- Promo codes (admin + super_admin) ----------
function PromoCodesPanel({ authHeader }) {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", description: "",
    audience: "customer", purpose: "booking",
    discount_type: "percent", amount: 10,
    min_subtotal: 0, max_uses: "", max_uses_per_user: "",
    allowed_categories: "",
    expires_at: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/promo-codes`, { headers: authHeader() });
      setCodes(r.data.promo_codes || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load promo codes"); }
  };

  useEffect(() => { if (open) load();   }, [open]);

  const onPurposeChange = (val) => {
    // Subscription promos can't target customers; nudge audience automatically
    let nextAudience = form.audience;
    if (val === "subscription" && form.audience === "customer") nextAudience = "business";
    let nextType = form.discount_type;
    // free_months only valid for subscription
    if (val !== "subscription" && nextType === "free_months") nextType = "percent";
    setForm({ ...form, purpose: val, audience: nextAudience, discount_type: nextType });
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        name: form.name.trim() || null,
        description: form.description.trim() || null,
        audience: form.audience,
        purpose: form.purpose,
        discount_type: form.discount_type,
        amount: parseFloat(form.amount),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_user: form.max_uses_per_user ? parseInt(form.max_uses_per_user) : null,
        allowed_categories: form.allowed_categories
          ? form.allowed_categories.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: true,
      };
      await axios.post(`${API}/admin/promo-codes`, payload, { headers: authHeader() });
      toast.success(`Promo "${payload.code}" created`);
      setShowForm(false);
      setForm({
        code: "", name: "", description: "",
        audience: "customer", purpose: "booking",
        discount_type: "percent", amount: 10,
        min_subtotal: 0, max_uses: "", max_uses_per_user: "",
        allowed_categories: "",
        expires_at: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { headers: authHeader() });
      toast.success(`Deactivated ${code}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isSubscription = form.purpose === "subscription";
  const isFreeMonths = form.discount_type === "free_months";

  return (
    <div className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="promo-codes-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Admin</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Promo codes</h2>
          <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--gf-text-muted)" }}>
            Discount codes for customers (booking discount) or businesses (subscription discount / free months).
            Supports percent, fixed-dollar, or free-months. Per-user limits, expiry, category whitelist, min subtotal.
          </p>
        </div>
        <button data-testid="promo-toggle-btn" onClick={() => setOpen((v) => !v)}
          className="rounded-full px-4 py-1.5 text-sm border" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
          {open ? "Close" : "Open"}
        </button>
      </div>

      {open && (
        <div className="mt-5">
          <div className="flex justify-end mb-3">
            <button data-testid="promo-new-btn" onClick={() => setShowForm((v) => !v)}
              className="rounded-full px-4 py-1.5 text-sm font-medium" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {showForm ? "Cancel" : "+ New promo"}
            </button>
          </div>
          {showForm && (
            <form onSubmit={create} className="rounded-xl border p-4 grid sm:grid-cols-2 gap-3 mb-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }} data-testid="promo-form">
              <Field label="Code (uppercase letters, digits, dash, underscore)" full>
                <input data-testid="promo-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="FRIENDS10" />
              </Field>
              <Field label="Display name (optional)">
                <input data-testid="promo-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Friends 10% off" />
              </Field>
              <Field label="Description (optional)">
                <input data-testid="promo-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Internal note or customer-facing tagline" />
              </Field>
              <Field label="Purpose">
                <select data-testid="promo-purpose-select" value={form.purpose} onChange={(e) => onPurposeChange(e.target.value)}>
                  <option value="booking">Customer booking discount</option>
                  <option value="subscription">Business subscription discount</option>
                </select>
              </Field>
              <Field label="Audience">
                <select data-testid="promo-audience-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                  {!isSubscription && <option value="customer">Customers only</option>}
                  <option value="business">Businesses only</option>
                  {!isSubscription && <option value="both">Both customers and businesses</option>}
                </select>
              </Field>
              <Field label="Discount type">
                <select data-testid="promo-type-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                  <option value="percent">Percent (%)</option>
                  <option value="fixed">Fixed dollar amount ($)</option>
                  {isSubscription && <option value="free_months">Free months</option>}
                </select>
              </Field>
              <Field label={isFreeMonths ? "Free months" : form.discount_type === "percent" ? "Amount (%)" : "Amount ($)"}>
                <input data-testid="promo-amount-input" required type="number"
                  step={isFreeMonths ? "1" : form.discount_type === "percent" ? "1" : "0.01"}
                  min={isFreeMonths ? "1" : "0.01"}
                  max={form.discount_type === "percent" ? "100" : isFreeMonths ? "24" : undefined}
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </Field>
              {!isFreeMonths && (
                <Field label="Min subtotal ($) — optional">
                  <input data-testid="promo-min-input" type="number" min="0" step="0.01" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} />
                </Field>
              )}
              <Field label="Max uses — optional">
                <input data-testid="promo-max-uses-input" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="leave blank for unlimited" />
              </Field>
              <Field label="Max uses per user — optional">
                <input data-testid="promo-max-uses-per-user-input" type="number" min="1" value={form.max_uses_per_user} onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })} placeholder="e.g. 1 for one-time-per-customer" />
              </Field>
              {!isSubscription && (
                <Field label="Allowed categories — optional (comma separated)" full>
                  <input data-testid="promo-allowed-categories-input" value={form.allowed_categories} onChange={(e) => setForm({ ...form, allowed_categories: e.target.value })} placeholder="Cleaning, Beauty, Auto" />
                </Field>
              )}
              <Field label="Expires at — optional" full>
                <input data-testid="promo-expires-input" type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </Field>
              <div className="sm:col-span-2 flex justify-end">
                <button data-testid="promo-create-btn" type="submit" disabled={busy}
                  className="rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                  {busy ? "Saving…" : "Create promo"}
                </button>
              </div>
            </form>
          )}

          {codes.length === 0 ? (
            <div className="rounded-xl border p-6 text-center text-sm" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }} data-testid="promo-empty">
              No promo codes yet.
            </div>
          ) : (
            <div className="grid gap-2" data-testid="promo-list">
              {codes.map((c) => (
                <div key={c.code} className="rounded-lg border p-3 flex items-start justify-between gap-3"
                  style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)", opacity: c.active ? 1 : 0.55 }}
                  data-testid={`promo-row-${c.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono" style={{ color: "var(--gf-text)" }}>{c.code}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5"
                        style={{ background: c.purpose === "subscription" ? "rgba(56,118,202,0.12)" : "var(--gf-surface-2)",
                          color: c.purpose === "subscription" ? "rgb(46,90,165)" : "var(--gf-accent)" }}>
                        {c.purpose === "subscription" ? "Subscription" : "Booking"}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>{c.audience}</span>
                    </div>
                    {(c.name || c.description) && <div className="text-xs mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{c.name || c.description}</div>}
                    <div className="text-xs mt-1" style={{ color: "var(--gf-text-soft)" }}>
                      {c.discount_type === "free_months"
                        ? `${c.amount} free month${c.amount === 1 ? "" : "s"}`
                        : c.discount_type === "percent" ? `${c.amount}% off` : `$${c.amount.toFixed(2)} off`}
                      {c.min_subtotal > 0 && ` · min $${c.min_subtotal.toFixed(2)}`}
                      {c.max_uses && ` · ${c.uses}/${c.max_uses} used`}
                      {!c.max_uses && ` · ${c.uses} used`}
                      {c.max_uses_per_user && ` · ${c.max_uses_per_user}/user`}
                      {c.expires_at && ` · expires ${new Date(c.expires_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  {c.active && (
                    <button data-testid={`promo-deactivate-${c.code}`} onClick={() => deactivate(c.code)}
                      className="text-xs underline" style={{ color: "var(--gf-text-muted)" }}>Deactivate</button>
                  )}
                  {!c.active && (
                    <span className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5" style={{ background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)", border: "1px solid var(--gf-border)" }}>Inactive</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, full, children }) {
  // small reusable field wrapper local to this file
  const cls = full ? "sm:col-span-2" : "";
  return (
    <div className={cls}>
      <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{label}</label>
      <div className="mt-1">
        {React.cloneElement(children, {
          className: (children.props.className || "") + " w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]",
          style: { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)", ...(children.props.style || {}) },
        })}
      </div>
    </div>
  );
}



function ActionModal({ business: b, action, onClose, onSubmit, submitting, authHeader }) {
  const [notes, setNotes] = React.useState("");
  const [reason, setReason] = React.useState("moderation");
  const [days, setDays] = React.useState(14);
  // Delete-permanent extras
  const [deleteReason, setDeleteReason] = React.useState("");
  const [confirmName, setConfirmName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [stepUpCode, setStepUpCode] = React.useState("");
  const [codeSending, setCodeSending] = React.useState(false);
  const [codeCooldown, setCodeCooldown] = React.useState(0);
  const [codeSent, setCodeSent] = React.useState(false);
  const isSuspend = action === "suspend";
  const isUnsuspend = action === "unsuspend";
  const isExtend = action === "extend_trial";
  const isEndTrial = action === "end_trial";
  const isDelete = action === "delete_permanent";

  React.useEffect(() => {
    if (codeCooldown <= 0) return;
    const t = setTimeout(() => setCodeCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [codeCooldown]);

  const sendStepUpCode = async () => {
    if (codeCooldown > 0 || codeSending) return;
    setCodeSending(true);
    try {
      await axios.post(`${API}/admin/step-up/send-code`, { purpose: "delete_business" }, { headers: authHeader() });
      setCodeSent(true);
      setCodeCooldown(60);
      toast.success("Step-up code sent to your admin email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally {
      setCodeSending(false);
    }
  };

  const TITLE = {
    suspend: `Suspend ${b.business_name}`,
    unsuspend: `Unsuspend ${b.business_name}?`,
    extend_trial: `Extend trial for ${b.business_name}`,
    end_trial: `End trial for ${b.business_name}?`,
    delete_permanent: `Permanently delete ${b.business_name}`,
  }[action] || "Confirm action";

  const submit = () => {
    if (isSuspend) {
      onSubmit({ reason, note: notes.trim() || null });
      return;
    }
    if (isUnsuspend) {
      onSubmit({ reason: "moderation", note: notes.trim() || null });
      return;
    }
    if (isEndTrial) {
      onSubmit({});
      return;
    }
    if (isExtend) {
      const n = parseInt(days, 10);
      if (!Number.isFinite(n) || n < 1 || n > 365) { toast.error("Days must be 1–365"); return; }
      onSubmit({ days: n });
      return;
    }
    if (isDelete) {
      if (deleteReason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
      if (confirmName.trim().toLowerCase() !== (b.business_name || "").trim().toLowerCase()) {
        toast.error("Typed business name doesn't match"); return;
      }
      if (!password) { toast.error("Password required"); return; }
      if (!stepUpCode || stepUpCode.trim().length !== 6) { toast.error("Enter the 6-digit step-up code"); return; }
      onSubmit({
        password,
        step_up_code: stepUpCode.trim(),
        confirm_business_name: confirmName.trim(),
        reason: deleteReason.trim(),
      });
      return;
    }
  };

  return (
    <div data-testid="action-modal" className="fixed inset-0 z-50 grid place-items-center px-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border p-5 sm:p-6 shadow-lg" style={{ background: "var(--gf-bg)", borderColor: "var(--gf-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>{TITLE}</h3>
          <button onClick={onClose} className="text-2xl leading-none" style={{ color: "var(--gf-text-muted)" }}>×</button>
        </div>

        {isSuspend && (
          <>
            <label className="block mt-4 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Reason</label>
            <select
              data-testid="action-modal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {SUSPEND_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="block mt-3 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Note (saved in audit log)</label>
            <textarea
              data-testid="action-modal-suspend-note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--gf-text)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              placeholder="What happened? (optional)"
            />
          </>
        )}

        {isUnsuspend && (
          <>
            <p className="text-sm mt-3" style={{ color: "var(--gf-text-soft)" }}>
              The business will be reactivated and can resume bookings.
            </p>
            <label className="block mt-3 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Note (optional)</label>
            <textarea
              data-testid="action-modal-unsuspend-note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </>
        )}

        {isEndTrial && (
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-soft)" }}>
            This ends the admin-granted trial immediately. Services will be hidden until the business activates a real subscription.
          </p>
        )}

        {isExtend && (
          <>
            <label className="block mt-4 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Extend by (days)</label>
            <input
              data-testid="action-modal-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1 w-32 rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </>
        )}

        {isDelete && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border p-3 text-sm" style={{ borderColor: "#7f1d1d", background: "rgba(220,38,38,0.06)", color: "#7f1d1d" }}>
              <div className="font-medium mb-1">⚠️ Permanent deletion — irreversible</div>
              <ul className="list-disc pl-5 space-y-0.5 text-xs leading-relaxed">
                <li>The business is anonymized and removed from public search, listings, favorites, and the business dashboard.</li>
                <li>All future bookings are cancelled; deposits already paid will be flagged for refund.</li>
                <li>Payment, refund, dispute, tax, and audit records are preserved for legal recordkeeping.</li>
                <li>The business cannot log in again — reversal is not possible via the app.</li>
              </ul>
            </div>
            <label className="block text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Reason for deletion (audit log)</label>
            <textarea
              data-testid="action-modal-delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={2}
              placeholder="e.g. Repeated fraudulent bookings, unresolvable disputes"
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <label className="block text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>
              Type the business name to confirm: <span className="normal-case" style={{ color: "var(--gf-text)" }}>{b.business_name}</span>
            </label>
            <input
              data-testid="action-modal-confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Business name (exactly)"
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <label className="block text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Your admin password</label>
            <input
              data-testid="action-modal-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Step-up code (from email)</label>
                <button
                  type="button"
                  data-testid="action-modal-send-code"
                  onClick={sendStepUpCode}
                  disabled={codeCooldown > 0 || codeSending}
                  className="text-xs hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: "var(--gf-accent)" }}
                >
                  {codeCooldown > 0 ? `Resend in ${codeCooldown}s` : codeSent ? "Resend code" : "Email me a code"}
                </button>
              </div>
              <input
                data-testid="action-modal-step-up-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={stepUpCode}
                onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mt-1 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none font-mono tracking-[6px]"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              />
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            data-testid="action-modal-cancel"
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            Cancel
          </button>
          <button
            data-testid="action-modal-confirm"
            onClick={submit}
            disabled={submitting}
            className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
            style={{
              background: (isSuspend || isDelete) ? "#dc2626" : "var(--gf-accent)",
              color: (isSuspend || isDelete) ? "#fff" : "var(--gf-on-accent)",
            }}
          >
            {submitting ? "…" : (
              isSuspend ? "Suspend" :
              isUnsuspend ? "Unsuspend" :
              isEndTrial ? "End trial" :
              isExtend ? "Extend trial" :
              isDelete ? "Delete permanently" :
              "Confirm"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function SafetyFlagsPanel({ authHeader }) {
  const [items, setItems] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState("open");
  const [busy, setBusy] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/safety-flags?status=${statusFilter}`, { headers: authHeader() });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load safety flags");
    } finally {
      setLoading(false);
    }
  }, [authHeader, statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  const resolve = async (it) => {
    const note = window.prompt("Resolution note (optional):", "") || "";
    setBusy(it.id);
    try {
      await axios.post(`${API}/admin/safety-flags/${it.id}/resolve`, { note }, { headers: authHeader() });
      toast.success("Resolved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    } finally {
      setBusy(null);
    }
  };

  const TYPE_LABEL = {
    wrong_person_report: "Wrong-person report (mobile booking)",
    repeated_no_show: "Repeated no-shows",
    repeated_complaints: "Repeated complaints",
    suspicious_activity: "Suspicious activity",
  };

  return (
    <section className="mt-12" data-testid="safety-flags-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-medium" style={{ color: "var(--gf-text-soft)" }}>SAFETY · NOTIFICATIONS</div>
          <h2 className="font-display text-2xl sm:text-3xl mt-1" style={{ color: "var(--gf-text)" }}>
            Reports &amp; suspicious-activity flags
            {items.length > 0 && statusFilter === "open" && (
              <span data-testid="safety-flags-badge" className="ml-3 align-middle inline-flex items-center justify-center rounded-full text-xs font-medium px-2 py-0.5"
                    style={{ background: "var(--gf-danger)", color: "#fff" }}>
                {items.length}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {["open", "resolved", "all"].map((s) => (
            <button key={s} data-testid={`safety-filter-${s}`} onClick={() => setStatusFilter(s)}
                    className="rounded-full px-3 py-1 text-xs border"
                    style={statusFilter === s
                      ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)", borderColor: "var(--gf-accent)" }
                      : { background: "transparent", color: "var(--gf-text)", borderColor: "var(--gf-border-strong)" }}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="rounded-xl border p-6 text-sm" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="safety-empty" className="rounded-xl border p-8 text-center text-sm"
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }}>
            No {statusFilter !== "all" ? statusFilter + " " : ""}safety reports right now.
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} data-testid={`safety-row-${it.id}`} className="rounded-2xl border p-5"
                 style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]"
                          style={{ background: it.status === "open" ? "var(--gf-danger)" : "var(--gf-surface-2)", color: it.status === "open" ? "#fff" : "var(--gf-text)" }}>
                      {it.status}
                    </span>
                    <h3 className="font-display text-lg" style={{ color: "var(--gf-text)" }}>{TYPE_LABEL[it.type] || it.type}</h3>
                  </div>
                  <div className="mt-1 text-sm" style={{ color: "var(--gf-text)" }}>{it.business_name} <span style={{ color: "var(--gf-text-muted)" }}>· {it.business_email}</span></div>
                  {it.note && (
                    <div className="mt-2 rounded-md border px-3 py-2 text-sm italic" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)", color: "var(--gf-text-soft)" }}>
                      &ldquo;{it.note}&rdquo;
                    </div>
                  )}
                  <div className="mt-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
                    Booking: {it.booking_id?.slice(0, 8)}… · Created: {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                    {it.resolved_at && <> · Resolved: {new Date(it.resolved_at).toLocaleString()}</>}
                  </div>
                  {it.resolution_note && (
                    <div className="mt-1 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>Resolution: {it.resolution_note}</div>
                  )}
                </div>
                {it.status === "open" && (
                  <button data-testid={`safety-resolve-${it.id}`} onClick={() => resolve(it)} disabled={busy === it.id}
                          className="rounded-full px-4 py-2 text-sm border disabled:opacity-50"
                          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                    {busy === it.id ? "…" : "Resolve"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}


// --------------------------------------------------------------------------
// Phase B — Ops panels (each renders one section of the operations dashboard)
// --------------------------------------------------------------------------
function Kpi({ label, value, testid, hint }) {
  return (
    <div className="rounded-xl border px-3 py-2" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface-2)" }}>
      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
      <div data-testid={testid} className="font-display text-2xl mt-0.5" style={{ color: "var(--gf-text)" }}>{value}</div>
      {hint && <div className="text-[10px] mt-1" style={{ color: "var(--gf-text-muted)" }}>{hint}</div>}
    </div>
  );
}

function StatusDot({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: ok ? "#15803d" : "#b91c1c" }}>
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: ok ? "#16a34a" : "#dc2626" }} />
      {label}
    </span>
  );
}

function useApi(url, authHeader, deps = []) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(url, { headers: authHeader() })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Load failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

function cents(v) { return `$${((v || 0) / 100).toFixed(2)}`; }

function OverviewPanel({ authHeader, refreshTick = 0 }) {
  const { data, loading, error } = useApi(`${API}/admin/overview`, authHeader, [refreshTick]);
  return (
    <div id="overview-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="overview-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Platform overview</h2>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }} data-testid="overview-error">{error}</div>}
      {data && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Active customers"  value={data.users?.active_customers ?? 0}  testid="ov-customers" />
          <Kpi
            label="Verified businesses"
            value={data.users?.verified_businesses ?? 0}
            testid="ov-businesses"
            hint={`${data.users?.subscribed_businesses ?? 0} subscribed · ${data.users?.stripe_ready_businesses ?? 0} Stripe-ready · ${data.users?.total_businesses ?? 0} total`}
          />
          <Kpi label="Total bookings"    value={data.bookings?.total ?? 0}          testid="ov-bookings"  hint={`+${data.bookings?.last_30d ?? 0} in last 30d`} />
          <Kpi label="Platform revenue"  value={cents(data.revenue?.platform_revenue_cents)} testid="ov-revenue" hint={`sub ${cents(data.revenue?.subscription_revenue_cents)} + fees ${cents(data.revenue?.platform_fee_cents)}`} />
          <Kpi label="Open cases"        value={data.moderation?.open_cases ?? 0}   testid="ov-open-cases" />
          <Kpi label="Open reports"      value={data.moderation?.open_reports ?? 0} testid="ov-open-reports" />
          <Kpi label="Failed payments"   value={data.payments?.failed_payments ?? 0} testid="ov-failed-payments" />
          <div className="rounded-xl border px-3 py-2 flex flex-col justify-center" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface-2)" }}>
            <StatusDot ok={data.services?.stripe_configured} label={`Stripe ${data.services?.stripe_configured ? "OK" : "off"}`} />
            <div className="h-1" />
            <StatusDot ok={data.services?.email_configured}  label={`Email ${data.services?.email_configured ? "OK" : "off"}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function FinancialPanel({ authHeader, refreshTick = 0 }) {
  const { data, loading, error } = useApi(`${API}/admin/financial`, authHeader, [refreshTick]);
  const byKind = data?.by_kind || {};
  return (
    <div id="financial-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="financial-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Financial</h2>
      <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Only rows where Stripe actually charged the customer count as revenue. Trial and pending checkouts are shown separately below.</p>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Subscription revenue (net)"  value={cents(byKind.subscription?.amount_cents)}   testid="fin-sub-rev" hint={`Gross ${cents(byKind.subscription?.gross_amount_cents)} · Refunds ${cents(byKind.subscription?.refund_amount_cents)}`} />
            <Kpi label="Platform fees"    value={cents(byKind.booking_downpayment?.platform_fee_cents)} testid="fin-platform-fees" />
            <Kpi label="Processing fees"       value={cents(byKind.booking_downpayment?.processing_fee_cents)} testid="fin-processing" />
            <Kpi label="Refunds paid"          value={cents((byKind.booking_downpayment?.refund_amount_cents || 0) + (byKind.subscription?.refund_amount_cents || 0))} testid="fin-refunds" />
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Trialing subs (not revenue)" value={data.trialing_subscription_rows ?? 0} testid="fin-trialing" hint="Trial checkouts — Stripe hasn't charged them yet" />
            <Kpi label="Pending subs (not revenue)"  value={data.pending_subscription_rows ?? 0}  testid="fin-pending" hint="Initiated / abandoned checkouts" />
            <Kpi label="Failed payments"       value={data.failed_payments ?? 0} testid="fin-failed" />
            <Kpi label="Open refund requests"  value={data.open_refund_requests ?? 0} testid="fin-refund-requests" />
          </div>
        </>
      )}
    </div>
  );
}

function StripePanel({ authHeader, refreshTick = 0 }) {
  const { data, loading, error } = useApi(`${API}/admin/stripe/overview`, authHeader, [refreshTick]);
  return (
    <div id="stripe-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="stripe-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Stripe</h2>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <div className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Connected accounts" value={data.connected_accounts} testid="stripe-connected" />
            <Kpi label="Charges enabled"    value={data.charges_enabled}    testid="stripe-charges" />
            <Kpi label="Payouts enabled"    value={data.payouts_enabled}    testid="stripe-payouts" />
            <Kpi label="Action required"    value={data.action_required}    testid="stripe-action-required" hint="Businesses stuck in KYC" />
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <StatusDot ok={data.api_key_configured}         label={`API key ${data.api_key_configured ? "OK" : "missing"}`} />
            <StatusDot ok={data.webhook_secret_configured}  label={`Webhook secret ${data.webhook_secret_configured ? "OK" : "missing"}`} />
            <a href="https://dashboard.stripe.com/" target="_blank" rel="noreferrer" className="text-xs underline" data-testid="stripe-dashboard-link" style={{ color: "var(--gf-accent)" }}>Open Stripe Dashboard →</a>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailHealthPanel({ authHeader, refreshTick = 0 }) {
  const { data, loading, error } = useApi(`${API}/admin/email/health`, authHeader, [refreshTick]);
  return (
    <div id="email-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="email-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Email health</h2>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <div className="mt-4">
          <StatusDot ok={data.resend_configured} label={`Resend ${data.resend_configured ? "configured" : "NOT configured"}`} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm">
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)" }} data-testid="email-sender-transactional">
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Transactional sender</div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--gf-text)" }}>{data.sender_transactional || "—"}</div>
            </div>
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)" }} data-testid="email-sender-security">
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Security sender</div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--gf-text)" }}>{data.sender_security || "—"}</div>
            </div>
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--gf-border)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Sender name</div>
              <div className="text-xs mt-1" style={{ color: "var(--gf-text)" }}>{data.sender_name || "—"}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <Kpi label="Password resets sent (30d)"   value={data.recent_password_resets_sent ?? 0}   testid="email-pw-sent" />
            <Kpi label="Password resets failed (30d)" value={data.recent_password_resets_failed ?? 0} testid="email-pw-failed" />
          </div>
        </div>
      )}
    </div>
  );
}

function AiModerationPanel({ authHeader, refreshTick = 0 }) {
  const [severity, setSeverity] = React.useState("");
  const url = `${API}/admin/ai/queue${severity ? `?severity=${severity}` : ""}`;
  const { data, loading, error } = useApi(url, authHeader, [severity, refreshTick]);
  return (
    <div id="ai-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="ai-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>AI moderation queue</h2>
        </div>
        <select
          data-testid="ai-severity-filter"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="rounded-full border bg-transparent px-3 py-1.5 text-xs"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Kpi label="Open AI cases"   value={data.open_ai_cases ?? 0}   testid="ai-open" />
            <Kpi label="Total AI cases"  value={data.total_ai_cases ?? 0}  testid="ai-total" />
            <div className="rounded-xl border px-3 py-2 flex items-center" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface-2)" }}>
              <StatusDot ok={data.ai_configured} label={`Claude ${data.ai_configured ? "OK" : "off"}`} />
            </div>
          </div>
          <div className="mt-4 space-y-2 max-h-80 overflow-auto">
            {(data.cases || []).length === 0 && <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>No cases match the filter.</div>}
            {(data.cases || []).map((c) => (
              <div key={c.id} data-testid={`ai-case-${c.id}`} className="rounded-lg border p-3 text-xs" style={{ borderColor: "var(--gf-border)" }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono opacity-70">{c.id?.slice(0, 8)}</span>
                  <span className="uppercase tracking-wider" style={{ color: c.ai_severity === "critical" ? "#b91c1c" : c.ai_severity === "high" ? "#c2410c" : "var(--gf-text-muted)" }}>
                    {c.ai_severity || "?"}
                  </span>
                </div>
                <div className="mt-1" style={{ color: "var(--gf-text)" }}>{c.summary || c.ai_summary || "(no summary)"}</div>
                <Link to={`/admin/cases`} className="text-[11px] underline" style={{ color: "var(--gf-accent)" }}>Open case →</Link>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CategoriesPanel({ authHeader, refreshTick = 0 }) {
  const [status, setStatus] = React.useState("");
  const url = `${API}/admin/categories${status ? `?status=${status}` : ""}`;
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { data, loading, error } = useApi(url, authHeader, [status, refreshKey, refreshTick]);
  const [renamingId, setRenamingId] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");

  const approve = async (id) => {
    try { await axios.post(`${API}/admin/categories/${id}/approve`, {}, { headers: authHeader() }); toast.success("Approved"); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitRename = async (id) => {
    try { await axios.post(`${API}/admin/categories/${id}/rename`, { new_name: renameValue }, { headers: authHeader() }); toast.success("Renamed"); setRenamingId(null); setRenameValue(""); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteCat = async (id) => {
    if (!window.confirm("Delete this category? It must have zero businesses.")) return;
    try { await axios.delete(`${API}/admin/categories/${id}`, { headers: authHeader() }); toast.success("Deleted"); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div id="categories-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="categories-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
          <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Categories</h2>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Approve pending categories, rename, or delete unused ones. Pending is internal-only — customers never see it.</p>
        </div>
        <select data-testid="cat-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border bg-transparent px-3 py-1.5 text-xs" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
          <option value="">All</option>
          <option value="pending">Pending only</option>
          <option value="approved">Approved only</option>
        </select>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Kpi label="Approved" value={data.approved_count ?? 0} testid="cat-approved-count" />
          <Kpi label="Pending"  value={data.pending_count ?? 0}  testid="cat-pending-count" />
          <Kpi label="Total"    value={(data.categories || []).length} testid="cat-total-count" />
        </div>
      )}
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <div className="mt-4 space-y-2 max-h-96 overflow-auto">
          {(data.categories || []).map((c) => (
            <div key={c.id} data-testid={`cat-${c.id}`} className="rounded-lg border p-3 flex items-center justify-between gap-2 text-sm" style={{ borderColor: "var(--gf-border)" }}>
              <div className="flex-1 min-w-0">
                {renamingId === c.id ? (
                  <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="w-full rounded border bg-transparent px-2 py-1 text-sm" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
                ) : (
                  <>
                    <div style={{ color: "var(--gf-text)" }}>{c.name}</div>
                    <div className="text-[11px]" style={{ color: c.status === "pending" ? "#c2410c" : "var(--gf-text-muted)" }}>
                      {c.status === "pending" ? "Pending review" : "Approved"} · {c.usage_count ?? 0} business{(c.usage_count ?? 0) === 1 ? "" : "es"}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                {c.status === "pending" && renamingId !== c.id && (
                  <button onClick={() => approve(c.id)} data-testid={`cat-approve-${c.id}`} className="text-xs rounded px-2 py-1 border" style={{ borderColor: "#15803d", color: "#15803d" }}>Approve</button>
                )}
                {renamingId === c.id ? (
                  <>
                    <button onClick={() => submitRename(c.id)} className="text-xs rounded px-2 py-1 border" style={{ borderColor: "var(--gf-accent)", color: "var(--gf-accent)" }}>Save</button>
                    <button onClick={() => { setRenamingId(null); setRenameValue(""); }} className="text-xs rounded px-2 py-1 border" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => { setRenamingId(c.id); setRenameValue(c.name); }} data-testid={`cat-rename-${c.id}`} className="text-xs rounded px-2 py-1 border" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Rename</button>
                )}
                {(c.usage_count ?? 0) === 0 && renamingId !== c.id && (
                  <button onClick={() => deleteCat(c.id)} data-testid={`cat-delete-${c.id}`} className="text-xs rounded px-2 py-1 border" style={{ borderColor: "#7f1d1d", color: "#7f1d1d" }}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsPanel({ authHeader, refreshTick = 0 }) {
  const { data, loading, error } = useApi(`${API}/admin/analytics`, authHeader, [refreshTick]);
  return (
    <div id="analytics-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="analytics-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Analytics</h2>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {data && (
        <div className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="New customers (30d)"  value={data.users?.new_customers_30d ?? 0}  testid="an-new-customers-30d" hint={`${data.users?.new_customers_7d ?? 0} in last 7d`} />
            <Kpi label="New businesses (30d)" value={data.users?.new_businesses_30d ?? 0} testid="an-new-businesses-30d" hint={`${data.users?.new_businesses_7d ?? 0} in last 7d`} />
            <Kpi label="Bookings (30d)"       value={data.bookings?.bookings_30d ?? 0}    testid="an-bookings-30d"      hint={`${data.bookings?.bookings_7d ?? 0} in last 7d`} />
            <Kpi label="Cancelled (30d)"      value={data.bookings?.cancelled_30d ?? 0}   testid="an-cancelled-30d" />
            <Kpi label="Refunds (30d)"        value={data.refunds?.refunds_30d ?? 0}      testid="an-refunds-30d" />
            <Kpi label="Repeat customers"     value={data.repeat_customers ?? 0}          testid="an-repeat" hint="2+ bookings" />
          </div>
          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--gf-text-muted)" }}>Top categories</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              {(data.top_categories || []).length === 0 && <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>No businesses yet.</div>}
              {(data.top_categories || []).map((c) => (
                <div key={c.name} className="rounded-lg border px-2 py-1.5" style={{ borderColor: "var(--gf-border)" }} data-testid={`an-cat-${c.name}`}>
                  <div className="truncate" style={{ color: "var(--gf-text)" }}>{c.name}</div>
                  <div className="text-[11px]" style={{ color: "var(--gf-text-muted)" }}>{c.count} business{c.count === 1 ? "" : "es"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ authHeader, refreshTick = 0 }) {
  const [refresh, setRefresh] = React.useState(0);
  const { data, loading, error } = useApi(`${API}/admin/settings`, authHeader, [refresh, refreshTick]);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, {
        customer_processing_fee_pct: parseFloat(form.customer_processing_fee_pct),
        platform_fee_pct: parseFloat(form.platform_fee_pct),
        support_email: form.support_email,
        ai_assistant_name: form.ai_assistant_name,
        maintenance_mode: !!form.maintenance_mode,
        feature_flags: form.feature_flags,
        legal_links: form.legal_links,
      }, { headers: authHeader() });
      toast.success("Settings saved");
      setRefresh((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div id="settings-panel" className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="settings-panel">
      <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super admin</div>
      <h2 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Platform settings</h2>
      {loading && <div className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
      {error && <div className="text-sm mt-3" style={{ color: "#b91c1c" }}>{error}</div>}
      {form && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Customer processing fee %</div>
            <input data-testid="settings-processing-fee" type="number" step="0.01" min="0" max="25" value={form.customer_processing_fee_pct} onChange={(e) => setForm({ ...form, customer_processing_fee_pct: e.target.value })} className="mt-1 w-full rounded border bg-transparent px-2 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Platform fee % (deposits)</div>
            <input data-testid="settings-platform-fee" type="number" step="0.01" min="0" max="25" value={form.platform_fee_pct} onChange={(e) => setForm({ ...form, platform_fee_pct: e.target.value })} className="mt-1 w-full rounded border bg-transparent px-2 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Support email</div>
            <input data-testid="settings-support-email" type="email" value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} className="mt-1 w-full rounded border bg-transparent px-2 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>AI assistant name</div>
            <input data-testid="settings-ai-name" value={form.ai_assistant_name || ""} onChange={(e) => setForm({ ...form, ai_assistant_name: e.target.value })} className="mt-1 w-full rounded border bg-transparent px-2 py-1" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
          </label>
          <label className="flex items-center gap-2 mt-2">
            <input data-testid="settings-maintenance-mode" type="checkbox" checked={!!form.maintenance_mode} onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })} />
            <span style={{ color: "var(--gf-text)" }}>Maintenance mode</span>
          </label>
          <div className="md:col-span-2 flex gap-2 justify-end mt-2">
            <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
          <div className="md:col-span-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
            Legal links (Terms/Privacy/Cookies) and feature flags are stored server-side and applied on next request. Contact support to edit those fields via API for now.
          </div>
        </div>
      )}
    </div>
  );
}
